import { z } from "zod";
import type { ValidationT } from "../i18n";

/**
 * Zod schema for the "New Statement" form (بیانیه).
 *
 * Required fields: title, summary, problem, solution, impact.
 * Optional fields: budget, timing (default ''), attachment_urls (default []).
 *
 * Factory form (R6 schema-factory): takes a `validation`-namespace translator so
 * error messages resolve through next-intl. Module-scope schemas can't call
 * useTranslations, so consumers build this via useMemo(() => makeStatementFormSchema(t), [t]).
 */
export const makeStatementFormSchema = (t: ValidationT) =>
  z.object({
    title: z.string().trim().min(3, t("min3Chars")).max(200, t("max200Chars")),
    summary: z
      .string()
      .trim()
      .min(10, t("min10Chars"))
      .max(2000, t("max2000Chars")),
    problem: z
      .string()
      .trim()
      .min(10, t("min10Chars"))
      .max(5000, t("max5000Chars")),
    solution: z
      .string()
      .trim()
      .min(10, t("min10Chars"))
      .max(5000, t("max5000Chars")),
    impact: z
      .string()
      .trim()
      .min(10, t("min10Chars"))
      .max(5000, t("max5000Chars")),
    budget: z.string().max(5000, t("max5000Chars")).optional().default(""),
    timing: z.string().max(2000, t("max2000Chars")).optional().default(""),
    attachment_urls: z.array(z.string().url()).max(1).default([]),
  });

export type StatementFormValues = z.infer<
  ReturnType<typeof makeStatementFormSchema>
>;
